import { effectScope } from "vue";

/**
 * Pinia Colada query composables must never be called inside a computed or a
 * render function. Each call runs refresh() on stale entries, registers a fresh
 * onScopeDispose on the calling scope and bumps the entry refCount. Done from
 * the breadcrumb computed that recomputes on every route/data change, this leaks
 * closures forever and writes reactive state mid-evaluation, which can spin the
 * Vue scheduler into an infinite update loop (the "Maximum recursive updates"
 * guard is dev-only, so production just freezes the tab).
 *
 * resolveBreadcrumbQuery memoizes each composable by reference and runs it once
 * inside a single detached effect scope, so the breadcrumb config functions only
 * read `.data.value` reactively afterwards. Activation stays lazy: a query is
 * created the first time a route actually needs that entity.
 *
 * Trade-off (accepted): once first used, the query stays active for the app
 * lifetime (e.g. refetch on window focus), since the detached scope is never
 * disposed.
 */
const queryScope = effectScope(true);
const instances = new Map<() => unknown, unknown>();

export function resolveBreadcrumbQuery<T>(composable: () => T): T {
  if (instances.has(composable)) {
    return instances.get(composable) as T;
  }

  const instance = queryScope.run(() => composable()) as T;
  instances.set(composable, instance);
  return instance;
}

interface BreadcrumbFilterOption {
  value: string;
  textValue?: string;
}

/**
 * Case-insensitive substring filter over an option's textValue (falling back to
 * its value). Used to make the breadcrumb entity-selector search actually filter
 * at the call-site, independent of reka-ui internals.
 */
export function filterBreadcrumbOptions<T extends BreadcrumbFilterOption>(
  options: T[],
  term: string
): T[] {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return options;

  return options.filter((option) =>
    (option.textValue ?? option.value).toLowerCase().includes(normalized)
  );
}
